import {
  API_URL,
  API_VERSION,
  MAX_CACHE_LAST_UPDATED_AT_SECONDS,
  SUPPORTED_BAPI_VERSION,
  USER_AGENT,
} from '../constants';
import {
  TokenVerificationError,
  TokenVerificationErrorAction,
  TokenVerificationErrorCode,
  TokenVerificationErrorReason,
} from '../errors';
import { runtime } from '../runtime';
import { joinPaths } from '../util/path';
import { retry } from '../util/shared';

type JsonWebKeyWithKid = JsonWebKey & { kid: string };

type JsonWebKeyCache = Record<string, JsonWebKeyWithKid>;

type RemoteJwksCache = {
  keys: JsonWebKeyCache;
  lastUpdatedAt: number;
};

/**
 * Remote JWKS caches, one per Clerk instance. A single process-wide cache keyed by `kid`
 * alone hands one instance's signing key to another instance's verification: a Clerk `kid`
 * is the instance id, the lookup short-circuits before `secretKey` is consulted, and
 * `verifyJwt` does not assert `iss`. That let a session token minted by instance B
 * authenticate against instance A in any process serving both.
 */
const remoteCaches = new Map<string, RemoteJwksCache>();

/**
 * The scope is held in memory only as a Map key. It is never logged or surfaced in errors.
 */
function getRemoteCache(scope: string): RemoteJwksCache {
  let cache = remoteCaches.get(scope);
  if (!cache) {
    // Evict expired scopes on new-scope creation so one-off scopes cannot grow the Map forever.
    for (const [key, entry] of remoteCaches) {
      if (cacheHasExpired(entry)) {
        remoteCaches.delete(key);
      }
    }
    cache = { keys: {}, lastUpdatedAt: 0 };
    remoteCaches.set(scope, cache);
  }
  return cache;
}

const PEM_HEADER = '-----BEGIN PUBLIC KEY-----';
const PEM_TRAILER = '-----END PUBLIC KEY-----';
const RSA_PREFIX = 'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA';
const RSA_SUFFIX = 'IDAQAB';

type LoadClerkJwkFromPemOptions = {
  kid: string;
  pem?: string;
};

/**
 * Loads a local PEM key usually from process.env and transform it to JsonWebKey format.
 * Derived fresh on every call: a cache keyed on `kid` (which comes from the untrusted token
 * header) served one instance's key to another instance's verifier, and derivation is cheap.
 */
export function loadClerkJwkFromPem(params: LoadClerkJwkFromPemOptions): JsonWebKey {
  const { kid, pem } = params;

  if (!pem) {
    throw new TokenVerificationError({
      action: TokenVerificationErrorAction.SetClerkJWTKey,
      message: 'Missing local JWK.',
      reason: TokenVerificationErrorReason.LocalJWKMissing,
    });
  }

  const modulus = pem
    .replace(/\r\n|\n|\r/g, '')
    .replace(PEM_HEADER, '')
    .replace(PEM_TRAILER, '')
    .replace(RSA_PREFIX, '')
    .replace(RSA_SUFFIX, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

  // https://datatracker.ietf.org/doc/html/rfc7517
  // The 'local-' kid prefix distinguishes locally derived JWKs from remote ones.
  const jwk: JsonWebKeyWithKid = { kid: `local-${kid}`, kty: 'RSA', alg: 'RS256', n: modulus, e: 'AQAB' };
  return jwk;
}

/**
 * @internal
 */
export type LoadClerkJWKFromRemoteOptions = {
  /**
   * @internal
   */
  kid: string;
  /**
   * @deprecated This cache TTL will be removed in the next major version. Specifying a cache TTL is a no-op.
   */
  jwksCacheTtlInMs?: number;
  /**
   * A flag to ignore the JWKS cache and always fetch JWKS before each JWT verification.
   */
  skipJwksCache?: boolean;
  /**
   * The Clerk Secret Key from the [**API keys**](https://dashboard.clerk.com/last-active?path=api-keys) page in the Clerk Dashboard.
   */
  secretKey?: string;
  /**
   * The [Clerk Backend API](https://clerk.com/docs/reference/backend-api){{ target: '_blank' }} endpoint.
   * @default 'https://api.clerk.com'
   */
  apiUrl?: string;
  /**
   * The version passed to the Clerk API.
   * @default 'v1'
   */
  apiVersion?: string;
};

/**
 *
 * Loads a key from JWKS retrieved from the well-known Frontend API endpoint of the issuer.
 * The result is also cached on the module level to avoid network requests in subsequent invocations.
 * The cache lasts up to 5 minutes.
 *
 * @param {Object} options
 * @param {string} options.kid - The id of the key that the JWT was signed with
 * @param {string} options.alg - The algorithm of the JWT
 * @returns {JsonWebKey} key
 */
export async function loadClerkJWKFromRemote(params: LoadClerkJWKFromRemoteOptions): Promise<JsonWebKey> {
  const { secretKey, apiUrl = API_URL, apiVersion = API_VERSION, kid, skipJwksCache } = params;

  const cache = getRemoteCache(`${apiUrl}|${apiVersion}|${secretKey ?? ''}`);

  if (skipJwksCache || cacheHasExpired(cache) || !cache.keys[kid]) {
    if (!secretKey) {
      throw new TokenVerificationError({
        action: TokenVerificationErrorAction.ContactSupport,
        message: 'Failed to load JWKS from Clerk Backend or Frontend API.',
        reason: TokenVerificationErrorReason.RemoteJWKFailedToLoad,
      });
    }
    const fetcher = () => fetchJWKSFromBAPI(apiUrl, secretKey, apiVersion) as Promise<{ keys: JsonWebKeyWithKid[] }>;
    const { keys } = await retry(fetcher);

    if (!keys || !keys.length) {
      throw new TokenVerificationError({
        action: TokenVerificationErrorAction.ContactSupport,
        message: 'The JWKS endpoint did not contain any signing keys. Contact support@clerk.com.',
        reason: TokenVerificationErrorReason.RemoteJWKFailedToLoad,
      });
    }

    keys.forEach(key => {
      cache.keys[key.kid] = key;
    });
    cache.lastUpdatedAt = Date.now();
  }

  const jwk = cache.keys[kid];

  if (!jwk) {
    // The available kids are deliberately omitted: they are instance ids, and enumerating
    // them would disclose which co-tenants are warm in a shared process.
    throw new TokenVerificationError({
      action: `Go to your Dashboard and validate your secret and public keys are correct. ${TokenVerificationErrorAction.ContactSupport} if the issue persists.`,
      message: `Unable to find a signing key in JWKS that matches the kid='${kid}' of the provided session token. Please make sure that the __session cookie or the HTTP authorization header contain a Clerk-generated session JWT.`,
      reason: TokenVerificationErrorReason.JWKKidMismatch,
    });
  }

  return jwk;
}

async function fetchJWKSFromBAPI(apiUrl: string, key: string, apiVersion: string) {
  if (!key) {
    throw new TokenVerificationError({
      action: TokenVerificationErrorAction.SetClerkSecretKey,
      message:
        'Missing Clerk Secret Key or API Key. Go to https://dashboard.clerk.com and get your key for your instance.',
      reason: TokenVerificationErrorReason.RemoteJWKFailedToLoad,
    });
  }

  const url = new URL(apiUrl);
  url.pathname = joinPaths(url.pathname, apiVersion, '/jwks');

  const response = await runtime.fetch(url.href, {
    headers: {
      Authorization: `Bearer ${key}`,
      'Clerk-API-Version': SUPPORTED_BAPI_VERSION,
      'Content-Type': 'application/json',
      'User-Agent': USER_AGENT,
    },
  });

  if (!response.ok) {
    const json = await response.json();
    const invalidSecretKeyError = getErrorObjectByCode(json?.errors, TokenVerificationErrorCode.InvalidSecretKey);

    if (invalidSecretKeyError) {
      const reason = TokenVerificationErrorReason.InvalidSecretKey;

      throw new TokenVerificationError({
        action: TokenVerificationErrorAction.ContactSupport,
        message: invalidSecretKeyError.message,
        reason,
      });
    }

    throw new TokenVerificationError({
      action: TokenVerificationErrorAction.ContactSupport,
      message: `Error loading Clerk JWKS from ${url.href} with code=${response.status}`,
      reason: TokenVerificationErrorReason.RemoteJWKFailedToLoad,
    });
  }

  return response.json();
}

function cacheHasExpired(cache: RemoteJwksCache) {
  // If the cache has expired, clear the value so we don't attempt to make decisions based on stale data
  const isExpired = Date.now() - cache.lastUpdatedAt >= MAX_CACHE_LAST_UPDATED_AT_SECONDS * 1000;

  if (isExpired) {
    cache.keys = {};
  }

  return isExpired;
}

type ErrorFields = {
  message: string;
  long_message: string;
  code: string;
};

const getErrorObjectByCode = (errors: ErrorFields[], code: string) => {
  if (!errors) {
    return null;
  }

  return errors.find((err: ErrorFields) => err.code === code);
};
